"use client";

import { getSiteContent } from "@/content/site";
import {
  Blog,
  FormDataProfile,
  hintWindowPreferences,
  UserInfo,
  UserPreferences,
} from "@/types";
import {
  dismissToastLater,
  getLatestBlogFromBe,
  getVersionFromBe,
  membershipChange,
  postFeatureProposal,
  saveUserPreferences,
  showLoadingToast,
  updateUserProfile,
} from "@/utils";
import { useCookiePreferenceState } from "@/hooks/useCookiePreferenceState";
import { useLanguage } from "@/context/LanguageContext";
import {
  CookiePreferences,
  createCookiePreferences,
} from "@/utils/cookiePreferences";
import { ChartPieIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

type AppContextType = {
  beVersion: string;
  feVersion: string;
  blog: Blog | null;
  isLoggedIn: boolean;
  handleLogout: () => Promise<void>;
  doLogin: boolean;
  setDoLogin: Dispatch<SetStateAction<boolean>>;
  setDoShowLoginForm: Dispatch<SetStateAction<boolean>>;
  doShowLoginForm: boolean;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: Dispatch<SetStateAction<boolean>>;
  createNewWatchDog: () => void;
  showLoginForm: (showLoginForm: boolean) => void;
  closeMobileMenu: () => void;
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  showPasswordRepeat: boolean;
  setShowPasswordRepeat: Dispatch<SetStateAction<boolean>>;
  PRODUCTS: readonly {
    name: string;
    description: string;
    href: string;
    icon: typeof ChartPieIcon;
  }[];
  PROFILE_ITEMS: readonly {
    name: string;
    href: string;
    icon: typeof CreditCardIcon;
  }[];
  SETTINGS_ITEMS: readonly {
    name: string;
    href: string;
    icon: typeof CreditCardIcon;
  }[];
  NAVBAR_ITEMS: readonly {
    name: string;
    href: string;
    icon: typeof CreditCardIcon;
  }[];
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  formData: FormDataProfile;
  setFormData: Dispatch<SetStateAction<FormDataProfile>>;
  saveProfileInfo: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  errorMessage: string;
  successMessage: string;
  userInfoGlobal: UserInfo | null;
  setUserInfoGlobal: Dispatch<SetStateAction<UserInfo | null>>;
  changeMembership: (
    isPaymentMethodChange: boolean,
  ) => (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  setSuccessMessage: Dispatch<SetStateAction<string>>;
  isDark: boolean;
  isCookiesAllowed: boolean;
  cookiePreferences: CookiePreferences;
  hasCookiePreference: boolean;
  isCookiePreferenceLoaded: boolean;
  isCookiePreferenceEditorOpen: boolean;
  openCookiePreferences: () => void;
  setCookiePreference: (preferences: CookiePreferences) => void;
  userPreferences: UserPreferences;
  updateUserPreferences: (
    preferences: Partial<UserPreferences>,
  ) => Promise<void>;
  toggleTheme: () => void;
  reportBug: (message: string) => Promise<void>;
};

const defaultCookiePreferences = createCookiePreferences({
  analyticalCookies: true,
  colorMode: "light",
  hasCookiePreference: false,
  technicalCookies: true,
});

const defaultUserPreferences: UserPreferences = {
  hideSuggestedBox: {},
  mode: "light",
  hideSection: [],
  hintWindows: {
    portfolioOverview: true,
    portfolioPerformance: true,
    watchdogOverview: true,
    watchdogResults: true,
    watchdogPriceDevelopment: true,
    analyticsViewTip: true,
    watchdogFavoritesTip: true,
    transactionImportTip: true,
    transactionHistoryImportTip: true,
  },
};

function normalizeUserPreferences(
  preferences?: Partial<UserPreferences> | null,
): UserPreferences {
  const rawHiddenSuggestions: unknown = preferences?.hideSuggestedBox;
  const hideSuggestedBox =
    rawHiddenSuggestions != null &&
    typeof rawHiddenSuggestions === "object" &&
    !Array.isArray(rawHiddenSuggestions)
      ? Object.fromEntries(
          Object.entries(rawHiddenSuggestions).filter(
            ([estateId, suggestions]) =>
              /^\d+$/.test(estateId) && Array.isArray(suggestions),
          ),
        )
      : {};
  const rawHiddenSections = Array.isArray(preferences?.hideSection)
    ? preferences.hideSection
    : [];
  const hideSection =
    rawHiddenSections.includes("watchdog") &&
    rawHiddenSections.includes("portfolio")
      ? []
      : rawHiddenSections;
  const rawHintWindows: Partial<UserPreferences["hintWindows"]> =
    preferences?.hintWindows != null &&
    typeof preferences.hintWindows === "object" &&
    !Array.isArray(preferences.hintWindows)
      ? preferences.hintWindows
      : {};
  const hintWindows = Object.fromEntries(
    hintWindowPreferences.map((hint) => [
      hint,
      rawHintWindows[hint] == null
        ? defaultUserPreferences.hintWindows[hint]
        : rawHintWindows[hint] === true,
    ]),
  ) as UserPreferences["hintWindows"];

  return {
    hideSuggestedBox,
    mode: preferences?.mode === "dark" ? "dark" : "light",
    hideSection,
    hintWindows,
  };
}

const defaultContext: AppContextType = {
  beVersion: "",
  feVersion: "",
  blog: null,
  isLoggedIn: false,
  handleLogout: async () => {},
  doLogin: true,
  setDoLogin: () => {},
  setDoShowLoginForm: () => {},
  doShowLoginForm: false,
  mobileMenuOpen: false,
  setMobileMenuOpen: () => {},
  createNewWatchDog: () => {},
  showLoginForm: () => {},
  closeMobileMenu: () => {},
  showPassword: false,
  setShowPassword: () => {},
  showPasswordRepeat: false,
  setShowPasswordRepeat: () => {},
  PRODUCTS: [],
  PROFILE_ITEMS: [],
  SETTINGS_ITEMS: [],
  NAVBAR_ITEMS: [],
  isLoading: false,
  setIsLoading: () => {},
  formData: {} as FormDataProfile,
  setFormData: () => {},
  saveProfileInfo: async () => {},
  deleteAccount: async () => {},
  errorMessage: "",
  successMessage: "",
  userInfoGlobal: null,
  setUserInfoGlobal: () => {},
  changeMembership:
    (isPaymentMethodChange: boolean = false) =>
    async (e: React.FormEvent<HTMLFormElement>) => {},
  setIsLoggedIn: () => {},
  setErrorMessage: () => {},
  setSuccessMessage: () => {},
  isDark: false,
  isCookiesAllowed: true,
  cookiePreferences: defaultCookiePreferences,
  hasCookiePreference: false,
  isCookiePreferenceLoaded: false,
  isCookiePreferenceEditorOpen: false,
  openCookiePreferences: () => {},
  setCookiePreference: () => {},
  userPreferences: defaultUserPreferences,
  updateUserPreferences: async () => {},
  toggleTheme: () => {},
  reportBug: async () => {},
};

const AppContext = createContext<AppContextType>(defaultContext);

const emptyProfileForm: FormDataProfile = {
  email: "",
  token: "",
  isActive: true,
  userName: null,
  subject: null,
  idNumber: null,
  address: null,
  oldPassword: null,
  newPassword: null,
  passwordRepeat: null,
  membership: null,
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [beVersion, setBeVersion] = useState(defaultContext.beVersion);
  const [blog, setBlog] = useState(defaultContext.blog);

  const feVersion = "v3.0.8";
  const router = useRouter();
  const { language, t } = useLanguage();
  const siteContent = getSiteContent(language);
  const copy = {
    cs: {
      logoutLoading: "Probíhá odhlašování",
      logoutSuccess: "Odhlášení proběhlo úspěšně",
      invalidMembership: "Neplatná hodnota členství.",
    },
    en: {
      logoutLoading: "Signing out",
      logoutSuccess: "Signed out successfully",
      invalidMembership: "Invalid membership value.",
    },
  }[language];

  const [userInfoGlobal, setUserInfoGlobal] = useState<UserInfo | null>(
    defaultContext.userInfoGlobal,
  );
  const [isLoggedIn, setIsLoggedIn] = useState(defaultContext.isLoggedIn);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(
    defaultContext.mobileMenuOpen,
  );
  const [doLogin, setDoLogin] = useState(defaultContext.doLogin);
  const [showPassword, setShowPassword] = useState(defaultContext.showPassword);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(
    defaultContext.showPasswordRepeat,
  );
  const [doShowLoginForm, setDoShowLoginForm] = useState(
    defaultContext.doShowLoginForm,
  );
  const [isLoading, setIsLoading] = useState(defaultContext.isLoading);
  const [errorMessage, setErrorMessage] = useState(defaultContext.errorMessage);
  const [successMessage, setSuccessMessage] = useState(
    defaultContext.successMessage,
  );
  const {
    cookiePreferences,
    hasCookiePreference,
    isCookiePreferenceEditorOpen,
    isCookiePreferenceLoaded,
    isCookiesAllowed,
    isDark,
    openCookiePreferences,
    setCookiePreference,
    setColorMode,
  } = useCookiePreferenceState(defaultCookiePreferences);

  const userPreferences = normalizeUserPreferences(
    userInfoGlobal?.preferences,
  );
  const latestUserPreferencesRef = useRef<UserPreferences>(
    defaultUserPreferences,
  );
  const preferenceSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const preferenceRevisionRef = useRef(0);

  useEffect(() => {
    if (userInfoGlobal?.preferences) {
      latestUserPreferencesRef.current = normalizeUserPreferences(
        userInfoGlobal.preferences,
      );
    }
  }, [userInfoGlobal?.preferences]);

  const updateUserPreferences = useCallback(
    async (preferenceUpdates: Partial<UserPreferences>) => {
      if (!userInfoGlobal?.token) return;

      const previousPreferences = latestUserPreferencesRef.current;
      const nextPreferences = {
        ...previousPreferences,
        ...preferenceUpdates,
      };
      const revision = preferenceRevisionRef.current + 1;
      preferenceRevisionRef.current = revision;
      latestUserPreferencesRef.current = nextPreferences;
      setUserInfoGlobal((current) =>
        current ? { ...current, preferences: nextPreferences } : current,
      );

      const email = userInfoGlobal.email;
      const token = userInfoGlobal.token;
      preferenceSaveQueueRef.current = preferenceSaveQueueRef.current.then(
        async () => {
          try {
            const response = await saveUserPreferences(
              {
                email,
                token,
                data: { preferences: nextPreferences },
              },
              token,
            );
            if (
              response.status < 200 ||
              response.status >= 300 ||
              response.data?.success === false ||
              !response.data?.data
            ) {
              throw new Error(
                response.data?.message ?? "Failed to save preferences",
              );
            }
            if (revision === preferenceRevisionRef.current) {
              const savedPreferences = normalizeUserPreferences(
                response.data.data,
              );
              latestUserPreferencesRef.current = savedPreferences;
              setUserInfoGlobal((current) =>
                current
                  ? { ...current, preferences: savedPreferences }
                  : current,
              );
            }
          } catch (error) {
            if (revision === preferenceRevisionRef.current) {
              latestUserPreferencesRef.current = previousPreferences;
              setUserInfoGlobal((current) =>
                current
                  ? { ...current, preferences: previousPreferences }
                  : current,
              );
            }
            setErrorMessage(String(error));
          }
        }
      );
      await preferenceSaveQueueRef.current;
    },
    [userInfoGlobal?.email, userInfoGlobal?.token],
  );

  useEffect(() => {
    const savedMode = userInfoGlobal?.preferences?.mode;
    if (savedMode === "dark" || savedMode === "light") {
      setColorMode(savedMode);
    }
  }, [setColorMode, userInfoGlobal?.preferences?.mode]);

  const toggleTheme = useCallback(() => {
    const nextMode = isDark ? "light" : "dark";
    setColorMode(nextMode);
    if (userInfoGlobal?.token) {
      void updateUserPreferences({ mode: nextMode });
    }
  }, [isDark, setColorMode, updateUserPreferences, userInfoGlobal?.token]);

  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handleLogout = useCallback(async () => {
    const toastId = showLoadingToast(copy.logoutLoading);
    dismissToastLater(toastId);

    setIsLoggedIn(false);
    setUserInfoGlobal(null);
    localStorage.removeItem("userInfo");
    setDoLogin(true);
    setDoShowLoginForm(true);
    setShowPassword(false);
    setShowPasswordRepeat(false);

    try {
      await fetch("/api/logout", {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-api-token": "hjkads5io5",
        },
      });
      toast.success(copy.logoutSuccess);
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      router.push("/");
    }
  }, [copy.logoutLoading, copy.logoutSuccess, router]);

  const createNewWatchDog = useCallback(() => {
    setDoLogin(false);
  }, []);

  const showLoginForm = useCallback((showLoginForm: boolean) => {
    setDoLogin(showLoginForm);
    setDoShowLoginForm(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
    setDoShowLoginForm(true);
    setShowPassword(false);
    setShowPasswordRepeat(false);
    setDoLogin(true);
  }, []);

  const [formData, setFormData] = useState<FormDataProfile>(emptyProfileForm);

  useEffect(() => {
    if (!userInfoGlobal) {
      setFormData(emptyProfileForm);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      membership: userInfoGlobal.membership ?? prev.membership,
    }));
  }, [userInfoGlobal]);

  const saveUserProfile = async (
    data: FormDataProfile,
    options: { updateLocalUser?: boolean } = {},
  ) => {
    const { updateLocalUser = true } = options;

    if (!userInfoGlobal?.token) return;

    setIsLoading(true);
    try {
      const payload = {
        email: data.email,
        token: data.token,
        isActive: data.isActive,
        userName: data.userName,
        subject: data.subject,
        idNumber: data.idNumber,
        address: data.address,
      };

      const response = await updateUserProfile(payload, userInfoGlobal.token);

      if (response.status === 200 && updateLocalUser) {
        setUserInfoGlobal((prev) => ({
          ...prev,
          ...response.data.data,
          token: prev?.token,
        }));
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfileInfo = useCallback(async () => {
    await saveUserProfile(formData);
  }, [formData, saveUserProfile]);

  const deleteAccount = useCallback(async () => {
    if (!userInfoGlobal?.token) return;

    await saveUserProfile(
      {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        isActive: false,
        userName: userInfoGlobal.userName ?? null,
        subject: userInfoGlobal.subject ?? null,
        idNumber: userInfoGlobal.idNumber ?? null,
        address: userInfoGlobal.address ?? null,
        oldPassword: null,
        newPassword: null,
        passwordRepeat: null,
        membership: userInfoGlobal.membership ?? null,
      },
      { updateLocalUser: false },
    );
    await handleLogout();
  }, [userInfoGlobal, saveUserProfile, handleLogout]);

  const changeMembership = useCallback(
    (isPaymentMethodChange: boolean) =>
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userInfoGlobal?.token) return;
        setIsLoading(true);

        try {
          const membership = formData.membership;
          if (typeof membership !== "string") {
            setErrorMessage(copy.invalidMembership);
            return;
          }

          const payload = {
            ...formData,
            isPaymentMethodChange,
          };

          const response = await membershipChange(
            payload,
            userInfoGlobal.token,
          );

          if (response.status === 200) {
            window._gopay.checkout({
              gatewayUrl: response.data.data.gw_url,
              inline: true,
            });
          }
        } catch (error) {
          setErrorMessage(String(error));
        } finally {
          setIsLoading(false);
        }
      },
    [copy.invalidMembership, formData, userInfoGlobal?.token],
  );

  const reportBug = useCallback(
    async (message: string) => {
      const payload: Record<string, any> = {
        data: { message },
      };

      if (userInfoGlobal?.email) {
        payload.email = userInfoGlobal.email;
      }

      if (userInfoGlobal?.token) {
        payload.token = userInfoGlobal.token;
      }

      try {
        const response = await postFeatureProposal(payload);

        if (response.status < 200 || response.status >= 300) {
          setErrorMessage(
            response.data?.message ?? t.common.genericSupportError,
          );
        }
      } catch {
        setErrorMessage(t.common.genericSupportError);
      }
    },
    [
      t.common.genericSupportError,
      userInfoGlobal?.email,
      userInfoGlobal?.token,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await getVersionFromBe();
        setBeVersion(response.data.data.version);

        const blogResponse = await getLatestBlogFromBe();
        setBlog(blogResponse.data.data.blog);
      } catch {
        setBeVersion("unknown");
      }
    })();
  }, []);

  return (
    <AppContext.Provider
      value={{
        beVersion,
        feVersion,
        blog,
        isLoggedIn,
        handleLogout,
        doLogin,
        setDoLogin,
        setDoShowLoginForm,
        doShowLoginForm,
        mobileMenuOpen,
        setMobileMenuOpen,
        createNewWatchDog,
        showLoginForm,
        closeMobileMenu,
        showPassword,
        setShowPassword,
        showPasswordRepeat,
        setShowPasswordRepeat,
        PRODUCTS: siteContent.productMenuItems,
        PROFILE_ITEMS: siteContent.profileItems,
        SETTINGS_ITEMS: siteContent.settingItems,
        NAVBAR_ITEMS: siteContent.navbarItems,
        isLoading,
        setIsLoading,
        formData,
        setFormData,
        saveProfileInfo,
        deleteAccount,
        errorMessage,
        successMessage,
        userInfoGlobal,
        setUserInfoGlobal,
        changeMembership,
        setIsLoggedIn,
        setErrorMessage,
        setSuccessMessage,
        isDark,
        isCookiesAllowed,
        cookiePreferences,
        hasCookiePreference,
        isCookiePreferenceLoaded,
        isCookiePreferenceEditorOpen,
        openCookiePreferences,
        setCookiePreference,
        userPreferences,
        updateUserPreferences,
        toggleTheme,
        reportBug,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
